<template>
  <div :class="{ editable, disabled, full }" class="sf-calculator">
    <div v-if="!full" class="edit" @click="toggleEditable">
      {{ $t('calculator.edit-loan') }}
      <pencil :class="{ active: editable }" class="pencil"/>
    </div>

    <field
      ref="amount"
      v-model="loanValue"
      :settings="loanSettings"
      :editable="editable"
      :label="$t('calculator.loan-amount')"/>

    <term
      v-if="showTerm"
      v-model="timeValue"
      :editable="editable"
      :label="$t('calculator.loan-days')"/>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import Pencil from '../../assets/icons/pencil.svg';
import Field from './calculator-field';
import Term from './calculator-term';
import { CALCULATOR_MODE, calculatorLimitFirst } from '~/modules/arithmetic/utils/consts';
import { WEB_ROUTES } from '~/app/shared/utils/web-config';

export default {
  components: { Term, Field, Pencil },
  props: {
    mode: {
      type: String,
      default: CALCULATOR_MODE.FULL,
    },
    limit: {
      type: String,
      default: '',
    },
    colors: {
      type: Object,
      required: true,
    },
    override: {
      type: Object,
      default: () => ({}),
    },
    infoVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editable: this.mode === CALCULATOR_MODE.FULL,
      size: 0,
    };
  },
  computed: {
    ...mapState('arithmetic', ['type', 'current']),
    ...mapGetters('arithmetic', [
      'authorized',
      'amount',
      'term',
      'additional',
      'displayed',
      'repeated',
      'disabled',
      'offerKey',
    ]),
    ...mapGetters('client', ['active']),
    loanValue: {
      get() {
        return this.amount.current;
      },
      set(value) {
        this.setValue({ key: 'amount', value });
      },
    },
    timeValue: {
      get() {
        const applyPages = [WEB_ROUTES.REGISTRATION_STEP_4, WEB_ROUTES.APPLICATION_APPLY];
        const isApplyPage = applyPages.some(page => this.$route.name.includes(page));

        if (isApplyPage && !this.additional) {
          return this.current.term;
        }

        return this.term.current;
      },
      set(value) {
        this.setValue({ key: 'term', value });
      },
    },
    loanSettings() {
      const processStyle = {
        background: this.colors.process,
      };

      const sliderStyle = {
        backgroundColor: this.colors.dot,
        color: this.colors.arrows,
      };

      return {
        processStyle,
        sliderStyle,
        ...this.amount,
        max: this.maxAmount,
      };
    },
    full() {
      return this.mode === CALCULATOR_MODE.FULL;
    },
    showTerm() {
      return !this.additional && this.infoVisible;
    },
    isClientNew() {
      return this.active && this.active.hasTypeFirst;
    },
    notAuthorizedNewClient() {
      return this.limit === calculatorLimitFirst && !this.authorized;
    },
    authorizedNewClient() {
      return this.authorized && this.isClientNew;
    },
    maxAmount() {
      if (this.notAuthorizedNewClient || this.authorizedNewClient) {
        return this.amount.first;
      } else {
        return this.amount.max;
      }
    },
  },
  async mounted() {
    await this.$nextTick();
    this.size = this.$refs.amount.size;
    this.$watch('$refs.amount.size', val => {
      this.size = val;
    });
  },
  methods: {
    ...mapMutations('arithmetic', ['setValue']),
    ...mapActions('arithmetic', ['getData', 'updateLimit']),
    refresh() {
      const { amount, term } = this.$refs;
      if (amount) amount.refresh();
      if (term) term.refresh();
    },
    async toggleEditable() {
      this.editable = !this.editable;
      await this.$nextTick();
      this.refresh();
      this.$emit('edit', this.editable);
    },
  },
};
</script>

<style lang="scss" scoped>
.sf-calculator {
  position: relative;
  width: 100%;
  outline: none !important;

  &.disabled {
    .value-label.amount {
      display: none !important;
    }

    .value-select.amount {
      display: none !important;
    }
  }
}

.edit {
  padding: 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  .pencil {
    width: 20px;
    height: 20px;

    /deep/ path {
      transition: fill 0.3s;
      fill: map-get($colors, secondary);
    }

    &.active /deep/ path {
      fill: map-get($colors, placeholder);
    }
  }
}
</style>
